import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, BookMarked, CheckCircle2, XCircle, BarChart2 } from 'lucide-react';
import apiService from '../../services/apiService';

/* ── 애니메이션 ───────────────────────────────────────────────────────────── */
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
`;
const scanFlash = keyframes`
  0%   { border-color: #f59e0b; box-shadow: 0 0 0 0 rgba(245,158,11,0.4); }
  70%  { border-color: #fbbf24; box-shadow: 0 0 0 8px rgba(245,158,11,0); }
  100% { border-color: #f59e0b; box-shadow: 0 0 0 0 rgba(245,158,11,0); }
`;

/* ── 레이아웃 ─────────────────────────────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif;
  padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #1e2030;
  flex-wrap: wrap;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer;
  padding: 8px; border-radius: 8px;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const TitleArea = styled.div`flex: 1; min-width: 180px;`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const PageSub = styled.p`margin: 2px 0 0; font-size: 12px; color: #64748b;`;

const Toolbar = styled.div`
  display: flex; gap: 10px; align-items: center; margin-bottom: 20px; flex-wrap: wrap;
`;
const Select = styled.select`
  padding: 8px 12px; background: #111827; border: 1px solid #374151;
  border-radius: 8px; color: #e2e8f0; font-size: 13px;
  &:focus { outline: none; border-color: #f59e0b; }
`;
const NumInput = styled.input`
  padding: 8px 10px; background: #111827; border: 1px solid #374151;
  border-radius: 8px; color: #e2e8f0; font-size: 13px; width: 90px; text-align: right;
  &:focus { outline: none; border-color: #f59e0b; }
  &:disabled { opacity: 0.4; }
`;
const Btn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 10px 18px; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; border: none;
  background: ${p => p.$primary ? 'rgba(245,158,11,0.15)' : '#1e2030'};
  color:      ${p => p.$primary ? '#fbbf24'               : '#94a3b8'};
  border: 1px solid ${p => p.$primary ? '#f59e0b' : '#374151'};
  &:hover  { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* ── 스캔 패널 ────────────────────────────────────────────────────────────── */
const ScanPanel = styled.div`
  background: #0d1117;
  border: 1px solid ${p => p.$active ? '#f59e0b' : '#1e2030'};
  border-radius: 14px; padding: 16px 20px; margin-bottom: 16px;
  animation: ${p => p.$active ? scanFlash : 'none'} 2s ease infinite;
`;
const ScanHeader = styled.div`display: flex; align-items: center; gap: 10px; margin-bottom: 10px;`;
const ScanDot = styled.div`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${p => p.$active ? '#fbbf24' : '#374151'};
  animation: ${p => p.$active ? pulse : 'none'} 1.2s ease infinite;
`;
const ScanLabel = styled.span`
  font-size: 11px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: 0.05em;
`;
const ScanStock = styled.div`
  font-size: 16px; font-weight: 700; color: #e2e8f0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const ScanCode  = styled.span`font-size: 11px; color: #475569; margin-left: 8px;`;
const ProgressTrack = styled.div`height: 5px; background: #1e2030; border-radius: 3px; overflow: hidden; margin-top: 10px;`;
const ProgressFill  = styled.div`
  height: 100%; border-radius: 3px; transition: width 0.4s ease;
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
`;
const ProgressMeta = styled.div`
  display: flex; justify-content: space-between; margin-top: 6px;
  font-size: 11px; color: #475569;
`;

/* ── 통계 카드 ────────────────────────────────────────────────────────────── */
const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px;
  @media (max-width: 560px) { grid-template-columns: repeat(2, 1fr); }
`;
const StatCard  = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 10px;
  padding: 12px 14px; text-align: center;
`;
const StatNum   = styled.div`font-size: 22px; font-weight: 800; color: ${p => p.$color || '#e2e8f0'};`;
const StatLabel = styled.div`font-size: 10px; color: #64748b; margin-top: 2px;`;

/* ── KIS 경고 배너 ────────────────────────────────────────────────────────── */
const KisWarn = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 14px; margin-bottom: 14px;
  background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.3);
  border-radius: 10px; font-size: 12px; color: #f59e0b; line-height: 1.5;
`;

/* ── 결과 테이블 ──────────────────────────────────────────────────────────── */
const ResultSection = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 14px; overflow: hidden;
`;
const ResultHeader = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px; border-bottom: 1px solid #1e2030; background: #0d1117;
`;
const ResultTitle = styled.span`font-size: 13px; font-weight: 700; color: #e2e8f0;`;
const HitsBadge   = styled.span`
  font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 99px;
  background: rgba(245,158,11,0.12); color: #fbbf24;
`;
const OverflowX = styled.div`overflow-x: auto;`;
const COLS = '40px 1fr 80px 80px 44px 44px 44px 68px 60px 68px';
const TH = styled.div`
  display: grid; grid-template-columns: ${COLS}; gap: 6px;
  padding: 10px 16px; font-size: 10px; color: #64748b; font-weight: 700;
  text-transform: uppercase; border-bottom: 1px solid #1e2030; min-width: 680px;
`;
const TR = styled.div`
  display: grid; grid-template-columns: ${COLS}; gap: 6px;
  padding: 11px 16px; border-bottom: 1px solid #1e2030;
  font-size: 12px; min-width: 680px; transition: background 0.15s;
  animation: ${fadeSlideIn} 0.35s ease;
  &:hover { background: #1a1f2e; }
  &:last-child { border-bottom: none; }
`;
const CondBadge = styled.span`
  font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 5px;
  background: ${p => p.$pass
    ? 'rgba(16,185,129,0.12)'
    : 'rgba(239,68,68,0.10)'};
  color: ${p => p.$pass ? '#10b981' : '#ef4444'};
`;
const ScoreBadge = styled.span`
  font-size: 11px; font-weight: 800; padding: 2px 8px; border-radius: 5px;
  background: rgba(245,158,11,0.12); color: #fbbf24;
`;
const WLBtn = styled.button`
  font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 5px;
  cursor: pointer; border: none;
  background: ${p => p.$added ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.12)'};
  color:      ${p => p.$added ? '#10b981'                : '#fbbf24'};
  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const EmptyHits = styled.div`padding: 32px; text-align: center; color: #374151; font-size: 13px;`;
const EmptyState = styled.div`
  text-align: center; padding: 60px 20px; color: #475569;
  background: #111827; border-radius: 14px;
`;

// ─── 컴포넌트 ─────────────────────────────────────────────────────────────────
function DtlScreenerPage() {
  const navigate = useNavigate();

  const [market,     setMarket]     = useState('ALL');
  const [minMcap,    setMinMcap]    = useState(1000);
  const [running,    setRunning]    = useState(false);
  const [done,       setDone]       = useState(false);
  const [current,    setCurrent]    = useState({ symbol: '', name: '', scanned: 0, total: 0 });
  const [hits,       setHits]       = useState([]);
  const [skipped,    setSkipped]    = useState(0);
  const [kisStats,   setKisStats]   = useState(null);
  const [wlStatus,   setWlStatus]   = useState({});   // symbol → 'added' | 'loading'
  const tableEndRef = useRef(null);

  useEffect(() => {
    if (hits.length > 0) {
      tableEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [hits.length]);

  // ── 스캔 실행 ──────────────────────────────────────────────────────────────
  const handleRun = async () => {
    setRunning(true);
    setDone(false);
    setHits([]);
    setSkipped(0);
    setKisStats(null);
    setWlStatus({});
    setCurrent({ symbol: '', name: '', scanned: 0, total: 0 });

    try {
      await apiService.runDtlScreener({ market, minMarketCap: minMcap }, (data) => {
        if (data.type === 'kisWarning') {
          setKisStats({ headerFailed: true, message: data.message });
        } else if (data.type === 'start') {
          setCurrent(p => ({ ...p, total: data.total }));
        } else if (data.type === 'progress') {
          setCurrent({ symbol: data.symbol, name: data.name, scanned: data.current, total: data.total });
        } else if (data.type === 'hit') {
          setHits(p => [...p, data]);
          // 스캔 중 워치리스트 자동 등재됨 표시
          setWlStatus(p => ({ ...p, [data.symbol]: 'added' }));
        } else if (data.type === 'skip') {
          setSkipped(p => p + 1);
        } else if (data.type === 'done') {
          if (data.kisStats) setKisStats(data.kisStats);
          setDone(true);
        }
      });
    } catch (e) {
      alert('스캐닝 오류: ' + e.message);
    } finally {
      setRunning(false);
    }
  };

  // ── 워치리스트 수동 추가 ────────────────────────────────────────────────────
  const handleAddWL = async (hit) => {
    setWlStatus(p => ({ ...p, [hit.symbol]: 'loading' }));
    try {
      await apiService.addDtlWatchlist({
        symbol:     hit.symbol,
        name:       hit.name,
        market:     hit.market,
        closePrice: hit.close
      });
      setWlStatus(p => ({ ...p, [hit.symbol]: 'added' }));
    } catch (e) {
      alert('워치리스트 추가 실패: ' + e.message);
      setWlStatus(p => ({ ...p, [hit.symbol]: null }));
    }
  };

  const pct = current.total > 0 ? (current.scanned / current.total) * 100 : 0;
  const fmt  = n => n != null ? Number(n).toLocaleString() : '-';
  const fmtMcap = v => {
    if (!v) return '-';
    if (v >= 10000) return (v / 10000).toFixed(1) + '조';
    return v.toLocaleString() + '억';
  };
  const showPanel = running || done || current.total > 0;

  return (
    <Page>
      {/* ── TopBar ── */}
      <TopBar>
        <BackBtn onClick={() => navigate('/labs')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>DTL-2Stage 스크리너</PageTitle>
          <PageSub>EMA 정배열 · 일목균형표 · VWAP · CCI ≥ 98 — 전 종목 발굴</PageSub>
        </TitleArea>
        <Btn onClick={() => navigate('/labs/dtl-screener/watchlist')}>
          <BookMarked size={14} /> 워치리스트
        </Btn>
        <Btn onClick={() => navigate('/labs/dtl-paper')}>
          <BarChart2 size={14} /> 가상매매
        </Btn>
      </TopBar>

      {/* ── 툴바 ── */}
      <Toolbar>
        <Select value={market} onChange={e => setMarket(e.target.value)} disabled={running}>
          <option value="ALL">전체 시장</option>
          <option value="KOSPI">KOSPI</option>
          <option value="KOSDAQ">KOSDAQ</option>
        </Select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8' }}>
          시총 ≥
          <NumInput
            type="number" value={minMcap} min={0} step={100}
            onChange={e => setMinMcap(Number(e.target.value))}
            disabled={running}
          />
          억원
        </label>
        <Btn $primary onClick={handleRun} disabled={running}>
          <RefreshCw size={14} style={{ animation: running ? 'spin 1s linear infinite' : 'none' }} />
          {running ? '스캔 중...' : '스캔 실행'}
        </Btn>
        {done && <span style={{ fontSize: 12, color: '#10b981' }}>✓ 완료</span>}
      </Toolbar>

      {/* ── KIS 경고 ── */}
      {kisStats?.headerFailed && (
        <KisWarn>
          <span>⚠</span>
          <div>
            <strong>KIS API 인증 실패</strong> — 시가총액을 실시간으로 가져오지 못했습니다.
            정적 데이터(stock_codes.json)로 대체되어 시총 필터 정확도가 낮을 수 있습니다.
            {kisStats.message && <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>{kisStats.message}</div>}
          </div>
        </KisWarn>
      )}
      {kisStats && !kisStats.headerFailed && kisStats.failures > 0 && (
        <KisWarn>
          <span>⚠</span>
          <div>
            <strong>KIS 시총 조회 일부 실패</strong> —{' '}
            {kisStats.attempts}건 중 {kisStats.failures}건 실패. 해당 종목은 정적 데이터 사용.
          </div>
        </KisWarn>
      )}

      {/* ── 실시간 스캔 패널 ── */}
      {showPanel && (
        <>
          <ScanPanel $active={running}>
            <ScanHeader>
              <ScanDot $active={running} />
              <ScanLabel>{running ? '실시간 스캔 중' : '스캔 완료'}</ScanLabel>
            </ScanHeader>
            <ScanStock>
              {running
                ? (current.name
                    ? <>{current.name}<ScanCode>{current.symbol}</ScanCode></>
                    : <span style={{ color: '#475569' }}>초기화 중...</span>)
                : <span style={{ color: '#10b981' }}>전체 {fmt(current.total)}종목 완료</span>
              }
            </ScanStock>
            <ProgressTrack>
              <ProgressFill style={{ width: `${pct}%` }} />
            </ProgressTrack>
            <ProgressMeta>
              <span>{fmt(current.scanned)} / {fmt(current.total)} 종목</span>
              <span>{pct.toFixed(1)}%</span>
            </ProgressMeta>
          </ScanPanel>

          <StatsRow>
            <StatCard>
              <StatNum $color="#94a3b8">{fmt(current.total)}</StatNum>
              <StatLabel>전체 종목</StatLabel>
            </StatCard>
            <StatCard>
              <StatNum $color="#60a5fa">{fmt(current.scanned)}</StatNum>
              <StatLabel>스캔 완료</StatLabel>
            </StatCard>
            <StatCard>
              <StatNum $color="#fbbf24">{hits.length}</StatNum>
              <StatLabel>발굴 종목</StatLabel>
            </StatCard>
            <StatCard>
              <StatNum $color="#374151">{fmt(skipped)}</StatNum>
              <StatLabel>조건 미달</StatLabel>
            </StatCard>
          </StatsRow>

          {/* ── 결과 테이블 ── */}
          <ResultSection>
            <ResultHeader>
              <ResultTitle>발굴 종목</ResultTitle>
              {hits.length > 0 && <HitsBadge>{hits.length}개</HitsBadge>}
              {running && hits.length === 0 && (
                <span style={{ fontSize: 11, color: '#475569', marginLeft: 4 }}>
                  조건 충족 종목 발견 시 즉시 표시
                </span>
              )}
            </ResultHeader>

            {hits.length > 0 ? (
              <OverflowX>
                <TH>
                  <span>#</span>
                  <span>종목</span>
                  <span>시가총액</span>
                  <span>종가</span>
                  <span>EMA</span>
                  <span>일목</span>
                  <span>VWAP</span>
                  <span style={{ textAlign: 'right' }}>CCI</span>
                  <span style={{ textAlign: 'center' }}>조건</span>
                  <span style={{ textAlign: 'center' }}>WL</span>
                </TH>
                {hits.map((h, i) => {
                  const wl = wlStatus[h.symbol];
                  return (
                    <TR key={h.symbol}>
                      <span style={{ color: '#475569', fontSize: 11 }}>{i + 1}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{h.name}</div>
                        <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>
                          {h.symbol} · {h.market}
                        </div>
                      </div>
                      <span style={{ color: '#94a3b8', fontSize: 11 }}>{fmtMcap(h.marketCap)}</span>
                      <span style={{ fontWeight: 700 }}>{fmt(h.close)}</span>

                      {/* 조건 배지 */}
                      <CondBadge $pass={h.conditions?.cond1}>
                        {h.conditions?.cond1 ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                      </CondBadge>
                      <CondBadge $pass={h.conditions?.cond2}>
                        {h.conditions?.cond2 ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                      </CondBadge>
                      <CondBadge $pass={h.conditions?.cond3}>
                        {h.conditions?.cond3 ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                      </CondBadge>

                      <span style={{
                        fontWeight: 700, textAlign: 'right',
                        color: h.cci >= 98 ? '#10b981' : '#ef4444'
                      }}>
                        {h.cci != null ? h.cci.toFixed(1) : '-'}
                      </span>

                      <div style={{ textAlign: 'center' }}>
                        <ScoreBadge>{h.conditionScore ?? 4}/4</ScoreBadge>
                      </div>

                      <div style={{ textAlign: 'center' }}>
                        <WLBtn
                          $added={wl === 'added'}
                          disabled={wl === 'loading' || wl === 'added'}
                          onClick={() => handleAddWL(h)}
                        >
                          {wl === 'added' ? '✓ 등재' : wl === 'loading' ? '...' : '+ WL'}
                        </WLBtn>
                      </div>
                    </TR>
                  );
                })}
                <div ref={tableEndRef} />
              </OverflowX>
            ) : (
              <EmptyHits>
                {running
                  ? '⏳ 조건 충족 종목을 탐색하고 있습니다...'
                  : '조건을 충족하는 종목이 없습니다.'}
              </EmptyHits>
            )}
          </ResultSection>
        </>
      )}

      {/* ── 초기 상태 ── */}
      {!showPanel && (
        <EmptyState>
          <div style={{ fontSize: 40, marginBottom: 14 }}>🔍</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            스캔 실행 버튼으로 DTL-2Stage 조건 종목을 발굴하세요
          </div>
          <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.9 }}>
            ① EMA(3&gt;5&gt;8&gt;10&gt;12) 정배열&nbsp;&nbsp;
            ② 일목균형표 정배열&nbsp;&nbsp;
            ③ VWAP(20&gt;72) 정배열&nbsp;&nbsp;
            ④ CCI(20) ≥ 98
          </div>
        </EmptyState>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Page>
  );
}

export default DtlScreenerPage;
